import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getChadawasBookingList,
  updateChadawaBookingList,
  updateChadawaBookingListStatus,
} from '../../services/chadawaBookingListService';

// Async thunk - fetch list
export const fetchChadawaBookingList = createAsyncThunk(
  'chadawaBookingList/fetchChadawaBookingList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getChadawasBookingList(params);

      // ✅ Always normalize from transactions + pagination
      return {
        bookings: response.transactions || [],
        totalPages: response.pagination?.totalPages || 1,
        totalItems: response.pagination?.totalItems || 0,
        currentPage: response.pagination?.currentPage || 1,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch booking list'
      );
    }
  }
);

// Async thunk - update booking
export const updateChadawaBookingListDate = createAsyncThunk(
  'chadawaBookingList/updateChadawaBookingListDate',
  async (updatedBooking, { rejectWithValue }) => {
    try {
      const response = await updateChadawaBookingList(updatedBooking);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error. message || 'Failed to update booking'
      );
    }
  }
);

// Async thunk - update booking status
export const updateChadawaBookingListStatusAsync = createAsyncThunk(
  'chadawaBookingList/updateChadawaBookingListStatus',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateChadawaBookingListStatus(payload);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update booking status'
      );
    }
  }
);

const chadawaBookingListSlice = createSlice({
  name: 'chadawaBookingList',
  initialState: {
    chadawaBookings: [],
    loading: false,
    error: null,
    totalPages: 0,
    totalItems: 0,
    currentPage: 1,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch list
      .addCase(fetchChadawaBookingList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChadawaBookingList.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const { bookings, totalPages, totalItems, currentPage } = action.payload;

        state.chadawaBookings = bookings;
        state.totalPages = totalPages;
        state.totalItems = totalItems;
        state.currentPage = currentPage;

      })
      .addCase(fetchChadawaBookingList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong while fetching booking list';
        state.chadawaBookings = [];
      })

      // Update booking
      .addCase(updateChadawaBookingListDate.fulfilled, (state, action) => {
        const updatedBooking = action.payload;
        const index = state.chadawaBookings.findIndex(
          (b) => b._id === updatedBooking._id || b.id === updatedBooking._id
        );
        if (index !== -1) {
          state.chadawaBookings[index] = updatedBooking;
        }
      })
      .addCase(updateChadawaBookingListDate.rejected, (state, action) => {
        state.error = action.payload || 'Something went wrong while updating booking';
      })

      // Update booking status
      .addCase(updateChadawaBookingListStatusAsync.fulfilled, (state, action) => {
        const updatedBooking = action.payload;
        const index = state.chadawaBookings.findIndex(
          (b) => b._id === updatedBooking._id || b.id === updatedBooking._id
        );
        if (index !== -1) {
          state.chadawaBookings[index] = updatedBooking;
        }
      })
      .addCase(updateChadawaBookingListStatusAsync.rejected, (state, action) => {
        state.error = action.payload || 'Something went wrong while updating booking status';
      });
  },
});

export default chadawaBookingListSlice.reducer;
